import './CreativeTray.scss';
import downloadjs from 'downloadjs';
import html2canvas from 'html2canvas';
import { useCallback, useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { useSetError } from '@src/redux/modals';
import { HeatMap } from '../../../utils/colors';
import { awaitJSON, GetS3SignedUrl2, MetaLambdaFetch } from '../../../utils/fetch-utils';
import { convertImageToBase64 } from '../../../utils/image-utils';
import { Appearance, Design, Size } from '../../designConstants';
import { DownloadDropdown } from '../../DownloadDropdown';
import { IconButton } from '../../IconButton';
import { AdInfoRow } from '../metrics-table-utils';
import {
  DISPLAYED_METRICS_LABELS,
  DisplayedMetrics,
  displayedMetricsFormatters
} from './creativeTrayUtils';

interface CreativeTrayProps {
  isOpen: boolean;
  onClose: () => void;
  status?: 'Active' | 'Paused' | 'Archived' | 'Deleted';
  launchDate?: string;
  metrics: DisplayedMetrics;
  heatMaps: Record<keyof DisplayedMetrics, HeatMap>;
  adInfo: AdInfoRow;
  kpiName?: string;
}

const BUCKET_NAME = 'bpm-cdn';
const MAX_WIDTH = 200;
const MAX_HEIGHT = 300;

export const CreativeTray = ({
  isOpen,
  onClose,
  status = 'Active',
  launchDate = '1/12/22',
  metrics,
  heatMaps,
  adInfo,
  kpiName
}: CreativeTrayProps) => {
  const [iframeFields, setIframeFields] = useState<{
    src: string;
    width: number;
    height: number;
  }>();
  const [signedImageUrl, setSignedImageUrl] = useState('');

  const setError = useSetError();
  const { company, ad_name, ad_id, account_id } = adInfo;
  const s3Key = `creativeAssets/metaThumbnails/${company}/${account_id}/${ad_id}.jpeg`;

  useEffect(() => {
    if (iframeFields) return;
    (async () => {
      try {
        const res = await MetaLambdaFetch('/getAdPreview', {
          method: 'POST',
          body: {
            accountID: account_id,
            businessManager: 'Tinuiti',
            adMetaId: ad_id
          }
        });
        const parsedRes = await awaitJSON(res);
        setIframeFields({
          src: parsedRes.src,
          width: Number(parsedRes.width),
          height: Number(parsedRes.height)
        });
      } catch {
        // Preview fetch failures are non-fatal — fall back to the static image.
      }
    })();
  }, [iframeFields, ad_id, account_id]);

  useEffect(() => {
    let isMounted = true;
    const getSignedUrl = async () => {
      if (!s3Key || !isMounted) return;
      try {
        const signedUrl = await GetS3SignedUrl2(BUCKET_NAME, s3Key);
        if (!isMounted) return;
        const base64Url = await convertImageToBase64(signedUrl);
        setSignedImageUrl(base64Url);
      } catch (error) {
        if (!isMounted) return;
        setError({
          title: "Couldn't load creative image",
          message: error.message,
          reportError: error
        });
      }
    };
    getSignedUrl();
    return () => {
      isMounted = false;
    };
  }, [s3Key, setError]);

  const downloadTray = useCallback(async () => {
    const contents = document.querySelector<HTMLElement>('.creative-tray-container');
    if (!contents) return;

    try {
      const canvas = await html2canvas(contents, {
        allowTaint: true,
        useCORS: true,
        scale: 2,
        onclone: (doc: Document) => {
          const wrapper = doc.querySelector<HTMLElement>('.iframe-outer');
          if (!wrapper) return;
          wrapper.style.width = '200px';
          wrapper.style.height = '200px';

          const iframe = wrapper.querySelector<HTMLElement>('.creative-iframe');
          if (iframe) {
            iframe.style.display = 'none';
          }

          const img = doc.createElement('img');
          img.src = signedImageUrl;
          img.alt = ad_name;
          img.style.width = '200px';
          img.style.height = '200px';
          wrapper.appendChild(img);

          const tray = doc.querySelector<HTMLElement>('.creative-tray-container');
          if (!tray) return;
          tray.style.height = `${tray.scrollHeight}px`;
          tray.style.width = `${tray.scrollWidth}px`;
        }
      });
      downloadjs(canvas.toDataURL('image/png'), `${ad_name}_CreativeTray.png`, 'image/png');
    } catch (error) {
      setError({ title: "Couldn't download creative", message: error.message, reportError: error });
    }
  }, [ad_name, signedImageUrl, setError]);

  if (!isOpen) return null;

  const w = iframeFields?.width ?? MAX_WIDTH;
  const h = iframeFields?.height ?? MAX_HEIGHT;
  const scale = Math.min(MAX_WIDTH / w, MAX_HEIGHT / h, 1);
  const displayWidth = Math.round(w * scale);
  const displayHeight = Math.round(h * scale);

  return (
    <div className='creative-tray-container'>
      <div data-html2canvas-ignore='true' className='creative-tray-header'>
        <DownloadDropdown
          size={Size.SM}
          design={Design.BRAND}
          menuOptions={['PNG']}
          onClickOptions={[downloadTray]}
          disabledMenuOptions={{ PNG: signedImageUrl === '' }}
        />
        <IconButton
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          className='close-button'
          onClick={onClose}
        >
          <MdClose />
        </IconButton>
      </div>
      <div className='creative-tray'>
        <div className='creative-thumbnail'>
          <div className='iframe-outer' style={{ width: displayWidth, height: displayHeight }}>
            <iframe
              className='creative-iframe'
              src={iframeFields?.src}
              title={ad_name}
              width={w}
              height={h}
              style={{
                width: w,
                height: h,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                border: 0
              }}
              data-html2canvas-ignore='true'
            />
          </div>
        </div>

        <div className='info-and-metrics'>
          <div className='creative-info-section'>
            <h2 className='creative-name'>{ad_name}</h2>
            <div className='creative-status-row'>
              <span className={`status-badge status-${status.toLowerCase()}`}>{status}</span>
              <span className='launch-date'>Launched: {launchDate}</span>
            </div>
          </div>

          <div className='metrics-section'>
            <div className='metrics-grid'>
              {DISPLAYED_METRICS_LABELS.map(({ key, label }) => {
                const value = metrics[key];
                const backgroundColor = heatMaps[key](value);
                const formattedValue = displayedMetricsFormatters(key)(value);

                if (label.includes('KPI') && kpiName) {
                  return (
                    <div key={key} className='metric-row'>
                      <div className='metric-label-and-kpi-name'>
                        <span className='metric-label'>{label}</span>
                        <span className='metric-kpi-name'>{kpiName}</span>
                      </div>
                      <span className='metric-value' style={{ backgroundColor }}>
                        {formattedValue}
                      </span>
                    </div>
                  );
                }

                return (
                  <div key={key} className='metric-row'>
                    <span className='metric-label'>{label}</span>
                    <span className='metric-value' style={{ backgroundColor }}>
                      {formattedValue}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
